import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Globe, Mail, ExternalLink } from 'lucide-react';

const footerLinks = {
  company: [
    { to: '/',                label: 'Home' },
    { to: '/ecosystem',       label: 'The Ecosystem' },
    { to: '/why-africa',      label: 'Why Africa' },
    { to: '/climate-impact',          label: 'Impact' },
  ],
  invest: [
    { to: '/opportunity',     label: 'The Opportunity' },
    { to: '/sankofa', label: 'Sankofa Foundry' },
    { href: '/documents/azura-whitepaper.pdf', label: 'White Paper ↗', external: true }
  ],
};

const Footer: React.FC = () => (
  <footer className="bg-bg-dark text-white">

    <div className="h-1 bg-accent-amber w-full" />

    {/* ── Main body ── */}
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-12">

        {/* Brand block */}
        <div className="md:col-span-5">
          {/* Logo */}
          <div className="flex items-center gap-2.5 mb-5">
            <div className="w-9 h-9 rounded-full bg-accent-amber flex items-center justify-center font-figtree font-black text-bg-dark text-sm">
              A
            </div>
            <span className="font-figtree font-black text-lg tracking-tight">
              Azura<span className="text-accent-amber">.</span>
            </span>
          </div>

          <p className="font-exo text-sm text-white/50 leading-relaxed mb-6 max-w-xs">
            Africa's electric mobility infrastructure, built to last. Fleet operations, Battery-as-a-Service, ride-hailing, and the continent's first indigenous EV OEM — launching Ibadan, 2026.
          </p>

          {/* Social / contact icons */}
          <div className="flex gap-2 mb-8">
            {[Globe, Mail, ExternalLink].map((Icon, i) => (
              <button key={i}
                className="w-9 h-9 rounded-full border border-white/10 bg-white/5 flex items-center justify-center text-white/40 hover:bg-accent-amber hover:text-bg-dark hover:border-accent-amber transition-all duration-200">
                <Icon size={14} />
              </button>
            ))}
          </div>

          {/* CTA block */}
          <div className="p-5 rounded-2xl border border-white/8 bg-white/[0.04]">
            <p className="font-mono text-[9px] tracking-[0.18em] text-white/35 uppercase mb-2">Investor Inquiries</p>
            <a href="mailto:invest@azuramobility.com"
              className="font-figtree font-bold text-sm text-accent-amber hover:text-white transition-colors block mb-3">
              invest@azuramobility.com
            </a>
            <a href="mailto:invest@azuramobility.com"
              className="inline-flex items-center gap-2 bg-accent-amber text-bg-dark font-figtree font-bold text-xs uppercase tracking-wider px-5 py-2.5 rounded-full hover:bg-accent-amber-dim transition-all ">
              Contact Us <ArrowRight size={13} />
            </a>
          </div>
        </div>

        {/* Nav columns */}
        <div className="md:col-span-3">
          <h4 className="font-mono text-[9px] tracking-[0.2em] uppercase text-white/30 mb-5">Company</h4>
          <ul className="space-y-3">
            {footerLinks.company.map(link => (
              <li key={link.to}>
                <Link to={link.to}
                  className="font-exo text-sm text-white/55 hover:text-white hover:pl-1 transition-all duration-200 flex items-center gap-1.5">
                  <span className="w-3 h-px bg-white/20 inline-block" />
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4">
          <h4 className="font-mono text-[9px] tracking-[0.2em] uppercase text-white/30 mb-5">Investors</h4>
          <ul className="space-y-3 mb-8">
            {footerLinks.invest.map(link => (
              <li key={link.label}>
                {'href' in link ? (
                  <a href={link.href}
                    target={link.external ? '_blank' : undefined}
                    rel={link.external ? 'noreferrer' : undefined}
                    className="font-exo text-sm text-white/55 hover:text-white hover:pl-1 transition-all duration-200 flex items-center gap-1.5">
                    <span className="w-3 h-px bg-white/20 inline-block" />
                    {link.label}
                  </a>
                ) : (
                  <Link to={(link as any).to}
                    className="font-exo text-sm text-white/55 hover:text-white hover:pl-1 transition-all duration-200 flex items-center gap-1.5">
                    <span className="w-3 h-px bg-white/20 inline-block" />
                    {link.label}
                  </Link>
                )}
              </li>
            ))}
          </ul>

          {/* Key numbers */}
          <div className="border-t border-white/[0.08] pt-6 grid grid-cols-2 gap-4">
            {[
              ['500', 'EVs Phase 1'],
              ['$6/day', 'Validated'],
              ['2042', 'Full OEM'],
              ['AZT', '$0.005 Round'],
            ].map(([v, l]) => (
              <div key={l}>
                <p className="font-figtree font-black text-lg text-accent-amber">{v}</p>
                <p className="font-mono text-[9px] tracking-widest text-white/30 uppercase mt-0.5">{l}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>

    {/* ── Bottom bar ── */}
    <div className="border-t border-white/[0.07]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex flex-col md:flex-row justify-between items-center gap-3">
        <p className="font-exo text-xs text-white/25">
          © 2026 Azura Mobility Limited. Ibadan, Nigeria. Whitepaper v2.0.
        </p>
      
      </div>
    </div>

  </footer>
);

export default Footer;